import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FolderIcon } from "@heroicons/react/24/solid";
import {
  buildGroupTree,
  hasHierarchyFields,
} from "../utils/offer-group-hierarchy";
import { LINE_DROP_MIME } from "../utils/offer-calc-item-groups";

const LINE_TYPE_ORDER = ["manual", "product", "hours", "calculation", "note"];

const lineTypeLabelKey = (lineType) => {
  switch (lineType) {
    case "manual":
      return "offer.calculation.workspace.line_type.manual";
    case "product":
      return "offer.calculation.workspace.line_type.product";
    case "hours":
      return "offer.calculation.workspace.line_type.hours";
    case "calculation":
      return "offer.calculation.workspace.line_type.calculation";
    case "note":
      return "offer.calculation.workspace.line_type.note";
    default:
      return "offer.calculation.workspace.line_type.other";
  }
};

const lineTypeIcon = (lineType) => {
  switch (lineType) {
    case "product":
      return "▣";
    case "hours":
      return "◷";
    case "calculation":
      return "◈";
    case "note":
      return "¶";
    case "manual":
      return "✎";
    default:
      return "•";
  }
};

const lineTitle = (item, index, t) => {
  const raw = (item?.description || "").trim();
  const description =
    item?.line_type === "note" || item?.item_type === "text"
      ? String(raw)
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim()
      : raw;
  if (description) return description;
  if (item?.product?.displayTitle) return item.product.displayTitle;
  if (item?.nested_calculation?.title) return item.nested_calculation.title;
  return t("offer.calculation.workspace.untitled_line", { n: index + 1 });
};

function StructureHeader({ t, onCollapse, onAddGroup }) {
  return (
    <div className="offer-calc-structure__header">
      <h4 className="offer-calc-structure__title">
        {t("offer.calculation.workspace.structure_title")}
      </h4>
      <div className="offer-calc-structure__header-actions">
        {onAddGroup ? (
          <button
            type="button"
            className="offer-calc-structure__add-group"
            onClick={() => onAddGroup(null)}
            title={t("offers.groups.add_top")}
          >
            +
          </button>
        ) : null}
        {onCollapse ? (
          <button
            type="button"
            className="offer-calc-structure__collapse"
            onClick={onCollapse}
            aria-label={t("offer.calculation.workspace.hide_structure")}
          >
            ‹
          </button>
        ) : null}
      </div>
    </div>
  );
}

function HierarchyNode({
  node,
  depth,
  collapsedIds,
  toggleCollapse,
  selectedGroupId,
  onSelectGroup,
  onEditGroup,
  onDeleteGroup,
  onAddGroup,
  onDropLineOnGroup,
  t,
}) {
  const id = node.id ?? node.clientId;
  const collapsed = collapsedIds.has(String(id));
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const selected = selectedGroupId != null && String(selectedGroupId) === String(id);
  const [dropActive, setDropActive] = useState(false);

  const handleDragOver = (e) => {
    if (!onDropLineOnGroup) return;
    const types = [...e.dataTransfer.types];
    if (!types.includes(LINE_DROP_MIME) && !types.includes("text/plain")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropActive(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropActive(false);
    }
  };

  const handleDrop = (e) => {
    if (!onDropLineOnGroup) return;
    e.preventDefault();
    setDropActive(false);
    const raw =
      e.dataTransfer.getData(LINE_DROP_MIME) ||
      e.dataTransfer.getData("text/plain");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload?.rowOrder == null) return;
      onDropLineOnGroup({
        rowOrder: payload.rowOrder,
        targetGroupId: node.id ?? null,
      });
    } catch {
      /* ignore malformed drag payload */
    }
  };

  return (
    <div className="offer-calc-structure__hierarchy-node">
      <div
        className={[
          "offer-calc-structure__hierarchy-row",
          selected ? "is-selected" : "",
          dropActive ? "is-drop-target" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ paddingLeft: 6 + depth * 12 }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <button
          type="button"
          className="offer-calc-structure__caret-btn"
          onClick={() => (hasChildren ? toggleCollapse(id) : undefined)}
          aria-expanded={hasChildren ? !collapsed : undefined}
          tabIndex={hasChildren ? 0 : -1}
        >
          <span className="offer-calc-structure__caret">
            {hasChildren ? (collapsed ? "▸" : "▾") : "·"}
          </span>
        </button>
        <button
          type="button"
          className="offer-calc-structure__hierarchy-label"
          onClick={() => onSelectGroup?.(node)}
          onDoubleClick={() => onEditGroup?.(node)}
          title={
            node.discipline
              ? `${groupDisplayName(node, t)} · ${node.discipline}`
              : groupDisplayName(node, t)
          }
        >
          <FolderIcon
            className="offer-calc-structure__folder"
            aria-label={t("offers.groups.folder_aria", {
              name: groupDisplayName(node, t),
            })}
          />
          <span className="offer-calc-structure__number" aria-hidden>
            {node.number || ""}
          </span>
          <span className="offer-calc-structure__item-title">
            {groupDisplayName(node, t)}
          </span>
          {node.discipline ? (
            <span className="offer-calc-structure__discipline">
              {node.discipline}
            </span>
          ) : null}
        </button>
        {onEditGroup ? (
          <button
            type="button"
            className="offer-calc-structure__edit-group"
            onClick={(e) => {
              e.stopPropagation();
              onEditGroup(node);
            }}
            title={t("offers.groups.edit")}
            aria-label={t("offers.groups.edit")}
          >
            <PencilIcon className="offer-calc-structure__edit-icon" aria-hidden />
          </button>
        ) : null}
        {onDeleteGroup ? (
          <button
            type="button"
            className="offer-calc-structure__delete-group"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteGroup(node);
            }}
            title={t("offers.groups.delete")}
            aria-label={t("offers.groups.delete")}
          >
            <TrashIcon className="offer-calc-structure__edit-icon" aria-hidden />
          </button>
        ) : null}
        {onAddGroup ? (
          <button
            type="button"
            className="offer-calc-structure__add-child"
            onClick={() => onAddGroup(node)}
            title={t("offers.groups.add_child")}
          >
            +
          </button>
        ) : null}
      </div>
      {hasChildren && !collapsed
        ? node.children.map((child) => (
            <HierarchyNode
              key={child.id ?? child.clientId ?? child.number}
              node={child}
              depth={depth + 1}
              collapsedIds={collapsedIds}
              toggleCollapse={toggleCollapse}
              selectedGroupId={selectedGroupId}
              onSelectGroup={onSelectGroup}
              onEditGroup={onEditGroup}
              onDeleteGroup={onDeleteGroup}
              onAddGroup={onAddGroup}
              onDropLineOnGroup={onDropLineOnGroup}
              t={t}
            />
          ))
        : null}
    </div>
  );
}

function groupDisplayName(node, t) {
  const title = (node?.title || "").trim();
  if (title) return title;
  if (node?.discipline) return node.discipline;
  return t("offers.groups.untitled");
}

/**
 * Left outline for line-based calculation workspace / offer group hierarchy.
 *
 * Modes:
 * - hierarchyGroups: Offer::Group tree with Claude numbers (1.0 / 1.1)
 * - items with hierarchy fields: treat group rows as tree when parent_id/number present
 * - fallback: group calculation rows by line_type (legacy)
 */
export default function OfferCalculationStructureTree({
  items = [],
  hierarchyGroups = null,
  selectedRowOrder = null,
  selectedGroupId = null,
  onSelectRow,
  onSelectGroup,
  onEditGroup,
  /** @deprecated alias — prefer onEditGroup */
  onRenameGroup,
  onDeleteGroup,
  onAddGroup,
  /** Drop calc line onto a group: ({ rowOrder, targetGroupId }) => void */
  onDropLineOnGroup,
  onCollapse,
  calculationV2Enabled = false,
}) {
  const editGroupHandler = onEditGroup || onRenameGroup;
  const { t } = useTranslation();
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  // Explicit hierarchy mode when hierarchyGroups is an array (incl. empty) —
  // empty must show empty-state + add, not fall through to line_type buckets.
  const hierarchyMode = Array.isArray(hierarchyGroups);

  const hierarchyTree = useMemo(() => {
    if (hierarchyMode) {
      return buildGroupTree(hierarchyGroups);
    }
    // Fallback: build from item rows that look like groups with hierarchy fields
    const groupRows = (items || []).filter(
      (item) =>
        item?.item_type === "group" ||
        item?.type === "group" ||
        item?.group_row === true
    );
    if (groupRows.length && hasHierarchyFields(groupRows)) {
      return buildGroupTree(
        groupRows.map((g, index) => ({
          id: g.id ?? g.draggableId ?? `row-${index}`,
          title: g.title || g.description,
          comment: g.comment || g.description,
          row_order: g.row_order ?? index,
          parent_id: g.parent_id ?? g.parentId ?? null,
          number: g.number,
          discipline: g.discipline,
        }))
      );
    }
    return null;
  }, [hierarchyGroups, hierarchyMode, items]);

  const typeGroups = useMemo(() => {
    if (hierarchyTree != null) return [];
    const byType = {};
    (items || []).forEach((item, index) => {
      const type = item?.line_type || "other";
      if (!byType[type]) byType[type] = [];
      byType[type].push({ item, index });
    });

    const orderedKeys = [
      ...LINE_TYPE_ORDER.filter((k) => byType[k]?.length),
      ...Object.keys(byType).filter((k) => !LINE_TYPE_ORDER.includes(k)),
    ];

    return orderedKeys.map((type) => ({
      type,
      rows: byType[type],
    }));
  }, [items, hierarchyTree]);

  const toggleGroup = (type) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleCollapse = (id) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const empty =
    hierarchyTree != null ? hierarchyTree.length === 0 : !items?.length;

  if (empty) {
    return (
      <nav
        className="offer-calc-structure"
        aria-label={t("offer.calculation.workspace.structure_title")}
      >
        <StructureHeader t={t} onCollapse={onCollapse} onAddGroup={onAddGroup} />
        <div className="offer-calc-workspace__pane-empty">
          <p className="offer-calc-workspace__pane-empty-title">
            {hierarchyMode
              ? t("offers.groups.empty_title")
              : t("offer.calculation.workspace.structure_empty_title")}
          </p>
          <p className="offer-calc-workspace__pane-empty-hint">
            {hierarchyMode
              ? t("offers.groups.empty_hint")
              : t("offer.calculation.workspace.structure_empty_hint")}
          </p>
        </div>
      </nav>
    );
  }

  if (hierarchyTree != null) {
    return (
      <nav
        className="offer-calc-structure"
        aria-label={t("offer.calculation.workspace.structure_title")}
      >
        <StructureHeader t={t} onCollapse={onCollapse} onAddGroup={onAddGroup} />
        <div className="offer-calc-structure__list">
          {hierarchyTree.map((node) => (
            <HierarchyNode
              key={node.id ?? node.clientId ?? node.number}
              node={node}
              depth={0}
              collapsedIds={collapsedIds}
              toggleCollapse={toggleCollapse}
              selectedGroupId={selectedGroupId}
              onSelectGroup={onSelectGroup}
              onEditGroup={editGroupHandler}
              onDeleteGroup={onDeleteGroup}
              onAddGroup={onAddGroup}
              onDropLineOnGroup={onDropLineOnGroup}
              t={t}
            />
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav
      className="offer-calc-structure"
      aria-label={t("offer.calculation.workspace.structure_title")}
    >
      <StructureHeader t={t} onCollapse={onCollapse} />

      <div className="offer-calc-structure__list">
        {typeGroups.map((group) => {
          const groupCollapsed = collapsedGroups.has(group.type);
          return (
            <div key={group.type} className="offer-calc-structure__group">
              <button
                type="button"
                className="offer-calc-structure__group-label"
                onClick={() => toggleGroup(group.type)}
              >
                <span className="offer-calc-structure__caret">
                  {groupCollapsed ? "▸" : "▾"}
                </span>
                <span className="offer-calc-structure__group-name">
                  {t(lineTypeLabelKey(group.type))}
                </span>
                <span className="offer-calc-structure__group-count">
                  {group.rows.length}
                </span>
              </button>
              {!groupCollapsed ? (
                <ul className="offer-calc-structure__items">
                  {group.rows.map(({ item, index }) => {
                    const rowOrder =
                      item.row_order !== undefined && item.row_order !== null
                        ? item.row_order
                        : index;
                    const selected =
                      selectedRowOrder != null &&
                      Number(selectedRowOrder) === Number(rowOrder);
                    const missingCost =
                      calculationV2Enabled &&
                      (item?.cost_total == null ||
                        item?.cost_total === "" ||
                        Number(item?.cost_total) === 0) &&
                      (Number(item?.purchase_price) || 0) <= 0 &&
                      (Number(item?.line_total) > 0 ||
                        Number(item?.sales_price) > 0);

                    return (
                      <li key={item.draggableId || item.id || `row-${rowOrder}`}>
                        <button
                          type="button"
                          className={[
                            "offer-calc-structure__item",
                            selected ? "is-selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => onSelectRow?.(rowOrder)}
                          aria-current={selected ? "true" : undefined}
                        >
                          <span className="offer-calc-structure__caret offer-calc-structure__caret--leaf">
                            ·
                          </span>
                          <span
                            className={`offer-calc-structure__icon is-${item?.line_type || "other"}`}
                            aria-hidden
                          >
                            {lineTypeIcon(item?.line_type)}
                          </span>
                          <span className="offer-calc-structure__item-title">
                            {lineTitle(item, index, t)}
                          </span>
                          {missingCost ? (
                            <span
                              className="offer-calc-structure__warn"
                              title={t(
                                "offer.calculation.workspace.missing_cost_line"
                              )}
                            >
                              ⚠
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
